import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Library, Search, Calendar, Building, Download, FileText, ChevronRight } from "lucide-react";

export default function PublicDocuments() {
    const [docs, setDocs] = useState([]);
    const [meta, setMeta] = useState(null);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({ category: "", field: "", agency: "" });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        const params = new URLSearchParams();
        if (search) params.set("q", search);
        if (filter.category) params.set("category", filter.category);
        if (filter.field) params.set("field", filter.field);
        if (filter.agency) params.set("agency", filter.agency);
        const { data } = await api.get(`/documents?${params}`);
        setDocs(data.documents || []);
        setLoading(false);
    };

    useEffect(() => { api.get("/documents-meta/options").then((r) => setMeta(r.data)); load(); }, []); // eslint-disable-line
    useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [search, filter]); // eslint-disable-line

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x">
                <div className="mb-8 fade-up">
                    <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] font-bold flex items-center gap-2 mb-2"><Library className="w-3 h-3" /> Thư viện</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold gold-underline inline-block">Văn bản pháp luật &amp; Tài liệu</h1>
                    <p className="text-slate-600 mt-7 max-w-3xl">Tra cứu Luật Dân số, Pháp lệnh, Nghị quyết, Thông tư, tài liệu truyền thông
                        và hướng dẫn chuyên môn về công tác dân số.</p>
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {/* Sidebar filter */}
                    <aside className="lg:col-span-3 space-y-4">
                        <FilterGroup title="Thể loại" items={meta?.categories} value={filter.category}
                            onChange={(v) => setFilter({ ...filter, category: v })} testIdPrefix="category" />
                        <FilterGroup title="Lĩnh vực" items={meta?.fields} value={filter.field}
                            onChange={(v) => setFilter({ ...filter, field: v })} testIdPrefix="field" />
                        <FilterGroup title="Cơ quan ban hành" items={meta?.agencies} value={filter.agency}
                            onChange={(v) => setFilter({ ...filter, agency: v })} testIdPrefix="agency" />
                    </aside>

                    {/* Main */}
                    <section className="lg:col-span-9 space-y-4">
                        <div className="card p-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input className="input pl-10" placeholder="Tìm văn bản theo tiêu đề hoặc số ký hiệu…"
                                    value={search} onChange={(e) => setSearch(e.target.value)} data-testid="public-doc-search" />
                            </div>
                        </div>

                        {loading ? (
                            <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="card h-24 shimmer" />)}</div>
                        ) : docs.length === 0 ? (
                            <div className="card p-10 text-center text-slate-500">
                                Không tìm thấy tài liệu phù hợp. Vui lòng thử bộ lọc khác.
                            </div>
                        ) : docs.map((d) => (
                            <article key={d.id} className="card card-hover p-5 flex gap-4 items-start" data-testid={`public-doc-${d.id}`}>
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--gold)] to-[var(--gold-soft)] flex items-center justify-center text-[var(--navy)] flex-shrink-0">
                                    <FileText className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                        {d.doc_number && <span className="badge badge-navy font-mono">{d.doc_number}</span>}
                                        <span className="badge badge-gold">{d.category}</span>
                                        <span className={`badge ${d.status === "Còn hiệu lực" ? "badge-success" : "badge-slate"}`}>{d.status}</span>
                                    </div>
                                    <h3 className="font-heading font-bold text-[var(--navy)] text-lg leading-snug">{d.title}</h3>
                                    {d.summary && <p className="text-sm text-slate-600 mt-1 line-clamp-2">{d.summary}</p>}
                                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-3 flex-wrap">
                                        {d.issued_at && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(d.issued_at).toLocaleDateString("vi-VN")}</span>}
                                        {d.agency && <span className="inline-flex items-center gap-1"><Building className="w-3 h-3" /> {d.agency}</span>}
                                        <span>· {d.field}</span>
                                    </div>
                                </div>
                                {d.file_url && (
                                    <a href={d.file_url} target="_blank" rel="noreferrer" className="btn-secondary !py-2 !px-3 text-xs" data-testid={`download-${d.id}`}>
                                        <Download className="w-4 h-4" /> Tải về
                                    </a>
                                )}
                            </article>
                        ))}
                    </section>
                </div>
            </div>
        </main>
    );
}

function FilterGroup({ title, items, value, onChange, testIdPrefix }) {
    if (!items?.length) return null;
    return (
        <div className="card p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">{title}</div>
            <div className="space-y-0.5">
                <button onClick={() => onChange("")}
                    className={`w-full text-left text-sm py-1.5 px-2 rounded-lg flex items-center justify-between ${
                        !value ? "bg-[var(--navy)] text-white" : "hover:bg-[var(--bg-alt)] text-slate-700"
                    }`} data-testid={`${testIdPrefix}-all`}>
                    Tất cả <ChevronRight className="w-3 h-3" />
                </button>
                {items.map((it) => (
                    <button key={it} onClick={() => onChange(it)}
                        className={`w-full text-left text-sm py-1.5 px-2 rounded-lg flex items-center justify-between ${
                            value === it ? "bg-[var(--gold)]/15 text-[var(--gold-deep)] font-semibold" : "hover:bg-[var(--bg-alt)] text-slate-700"
                        }`} data-testid={`${testIdPrefix}-${it.replace(/\s+/g, "-").toLowerCase()}`}>
                        <span className="truncate">{it}</span>
                        <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    </button>
                ))}
            </div>
        </div>
    );
}
